/**
 * Candela — Favorites (localStorage wishlist)
 *
 * Stores an array of { id, handle } objects in localStorage.
 * Exposes a global CandelaFavorites object for use across pages.
 *
 * Usage:
 *   CandelaFavorites.add(productId, handle)
 *   CandelaFavorites.remove(productId)
 *   CandelaFavorites.toggle(productId, handle) → returns { added: bool }
 *   CandelaFavorites.has(productId) → bool
 *   CandelaFavorites.getAll() → [{ id, handle }]
 *   CandelaFavorites.count() → number
 *   CandelaFavorites.onChange(callback) → unsubscribe function
 */
(function () {
  const STORAGE_KEY = 'candela-favorites';
  const listeners = [];

  function read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function write(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    listeners.forEach(function (cb) { cb(items); });
  }

  function add(productId, handle) {
    const items = read();
    const id = String(productId);
    if (items.some(function (item) { return String(item.id) === id; })) return;
    items.push({ id: id, handle: handle });
    write(items);
  }

  function remove(productId) {
    const id = String(productId);
    const items = read().filter(function (item) { return String(item.id) !== id; });
    write(items);
  }

  function toggle(productId, handle) {
    if (has(productId)) {
      remove(productId);
      return { added: false };
    }
    add(productId, handle);
    return { added: true };
  }

  function has(productId) {
    const id = String(productId);
    return read().some(function (item) { return String(item.id) === id; });
  }

  function getAll() {
    return read();
  }

  function count() {
    return read().length;
  }

  function onChange(callback) {
    listeners.push(callback);
    return function () {
      var idx = listeners.indexOf(callback);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }

  window.CandelaFavorites = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    getAll: getAll,
    count: count,
    onChange: onChange
  };

  /* --- Nav "Favorites (n)" link: hidden until something is favorited --- */
  function updateFavCounts() {
    var c = count();
    document.querySelectorAll('.candela-nav__fav-count').forEach(function (el) {
      el.textContent = c;
    });
    document.querySelectorAll('.candela-nav__favorites-link').forEach(function (link) {
      link.hidden = c === 0;
    });
  }

  /* --- Auto-init favorite buttons on page load --- */
  document.addEventListener('DOMContentLoaded', function () {
    initFavoriteButtons();
    updateFavCounts();
    onChange(updateFavCounts);

    /* Re-init on Shopify section reload (theme editor) */
    document.addEventListener('shopify:section:load', function () {
      initFavoriteButtons();
      updateFavCounts();
    });
  });

  function initFavoriteButtons() {
    document.querySelectorAll('.candela-product__favorite-btn[data-product-id]').forEach(function (btn) {
      var productId = btn.dataset.productId;
      var handle = btn.dataset.productHandle;
      var textEl = btn.querySelector('.candela-product__favorite-text');

      /* Set initial state */
      updateButtonState(btn, textEl, has(productId));

      /* Prevent duplicate listeners */
      if (btn.dataset.favInit) return;
      btn.dataset.favInit = 'true';

      btn.addEventListener('click', function () {
        var result = toggle(productId, handle);
        updateButtonState(btn, textEl, result.added);
      });
    });

  }

  function updateButtonState(btn, textEl, isFavorited) {
    if (isFavorited) {
      btn.classList.add('is-favorited');
      if (textEl) textEl.textContent = 'Added to Favorites \u2713';
    } else {
      btn.classList.remove('is-favorited');
      if (textEl) textEl.textContent = btn.getAttribute('aria-label') || 'Add to Favorite+';
    }
  }
})();
